/**
 * TransactionStatisticsPage (통계 화면)
 *
 * 기간별 소비 패턴 분석 및 차트 표시
 * - 주/월/년 단위 기간 선택
 * - 수입/지출 요약
 * - 카테고리별 분포 파이차트
 * - 시계열 추이 차트 (라인/막대 토글)
 * - TOP 5 카테고리 막대차트
 * - 차트 클릭 시 상세 거래 내역 모달
 */

import { useState, useMemo, useCallback } from 'react'
import { usePeriodNavigation } from '../hooks/usePeriodNavigation'
import { useStatisticsData } from '../hooks/useStatisticsData'
import { PeriodSelector } from '../components/statistics/PeriodSelector'
import { StatisticsSummary } from '../components/statistics/StatisticsSummary'
import { CategoryPieChart } from '../components/statistics/CategoryPieChart'
import { SpendingTrendChart } from '../components/statistics/SpendingTrendChart'
import { TopCategoriesChart } from '../components/statistics/TopCategoriesChart'
import { ChartDetailModal } from '../components/statistics/ChartDetailModal'
import type { ChartDetailData, CategoryStat, TimeSeriesPoint, ChartDataKey } from '../types/Statistics'
import { usePaymentMethods } from '@/domains/payment/hooks/usePaymentMethods'
import { useUsers } from '@/domains/user/hooks/useUsers'
import { useCategories } from '@/domains/category/hooks/useCategories'
import type { Category } from '@/domains/category/types/Category'

export function TransactionStatisticsPage() {
  // 기간 네비게이션 관리
  const {
    period,
    periodType,
    goToPrevious,
    goToNext,
    goToToday,
    changePeriodType,
  } = usePeriodNavigation('month')

  // 통계 데이터 조회
  const { data, loading, error } = useStatisticsData(period)

  // 관련 데이터 병렬 로딩 (ChartDetailModal에서 사용)
  const { paymentMethods } = usePaymentMethods()
  const { users } = useUsers()
  const { expenseCategories, incomeCategories } = useCategories()

  // Categories flat 배열 생성
  const allCategories = useMemo(() => {
    const result: Category[] = []
    for (const major of [...expenseCategories, ...incomeCategories]) {
      result.push(major)
      if (major.children) {
        result.push(...major.children)
      }
    }
    return result
  }, [expenseCategories, incomeCategories])

  // 차트 상세 모달 상태
  const [detailData, setDetailData] = useState<ChartDetailData | null>(null)

  // 차트 표시 데이터 상태 (period 변경 시에도 유지)
  const [visibleChartData, setVisibleChartData] = useState<ChartDataKey[]>(['income', 'expense'])

  /**
   * 카테고리 차트 클릭 핸들러
   * - 카테고리별 거래 내역을 모달로 표시
   */
  const handleCategoryClick = useCallback((stat: CategoryStat) => {
    setDetailData({
      type: 'category',
      title: `${stat.categoryIcon} ${stat.categoryName}`,
      subtitle: `${period.label} · ${stat.transactionCount}건`,
      transactions: stat.transactions,
      totalAmount: stat.amount,
    })
  }, [period.label])

  /**
   * 시계열 차트 클릭 핸들러
   * - 특정 날짜/월의 거래 내역을 모달로 표시
   */
  const handleTimeSeriesClick = useCallback((point: TimeSeriesPoint) => {
    if (!data) return

    // 해당 기간의 지출 거래만 필터링
    const transactions = data.categoryStats
      .flatMap(stat => stat.transactions)
      .filter(tx => {
        // week/month: YYYY-MM-DD 비교
        // year: YYYY-MM 비교
        if (periodType === 'year') {
          return tx.date.startsWith(point.date)
        } else {
          return tx.date === point.date
        }
      })
      // 중복 제거 (같은 거래가 여러 카테고리에 있을 수 있음)
      .filter((tx, index, self) => self.findIndex(t => t.id === tx.id) === index)

    setDetailData({
      type: 'timeseries',
      title: point.label,
      subtitle: `${period.label} · ${transactions.length}건`,
      transactions,
      totalAmount: point.expense,
    })
  }, [data, period.label, periodType])

  /**
   * 로딩 상태
   */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p>통계 데이터를 불러오는 중...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * 에러 상태
   */
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="text-center">
              <span className="text-4xl mb-4 block">⚠️</span>
              <p className="text-lg text-red-600 dark:text-red-400 mb-2">
                데이터를 불러오는 중 오류가 발생했습니다
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {error.message}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * 데이터 없음 상태
   */
  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-6xl mx-auto">
          <PeriodSelector
            period={period}
            periodType={periodType}
            onChangePeriodType={changePeriodType}
            onPrevious={goToPrevious}
            onNext={goToNext}
            onToday={goToToday}
          />

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12">
            <div className="text-center">
              <span className="text-6xl mb-4 block">📊</span>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-2">
                통계 데이터가 없습니다
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {period.label}에 등록된 거래가 없습니다
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * 정상 데이터 표시
   */
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-6xl mx-auto">
        {/* 기간 선택 */}
        <PeriodSelector
          period={period}
          periodType={periodType}
          onChangePeriodType={changePeriodType}
          onPrevious={goToPrevious}
          onNext={goToNext}
          onToday={goToToday}
        />

        {/* 요약 카드 */}
        <StatisticsSummary summary={data.summary} />

        {/* 차트 영역 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* 1. 카테고리 분포 파이차트 - 왼쪽 */}
          <CategoryPieChart
            data={data.categoryStats}
            onCategoryClick={handleCategoryClick}
          />

          {/* 2. TOP 카테고리 - 오른쪽 (순서 변경됨) */}
          <TopCategoriesChart
            data={data.categoryStats}
            onCategoryClick={handleCategoryClick}
          />

          {/* 3. 지출 추이 차트 - 전체 너비 (순서 변경됨) */}
          <div className="lg:col-span-2">
            <SpendingTrendChart
              data={data.timeSeriesData}
              onPointClick={handleTimeSeriesClick}
              visibleData={visibleChartData}
              onVisibleDataChange={setVisibleChartData}
            />
          </div>
        </div>

        {/* 상세 거래 내역 모달 */}
        <ChartDetailModal
          data={detailData}
          paymentMethods={paymentMethods}
          users={users}
          categories={allCategories}
          onClose={() => setDetailData(null)}
        />
      </div>
    </div>
  )
}
